// The landing gate. A brand-new visitor is sent to /welcome first; once they either sign in
// or choose "Explore as guest" we remember it and never redirect them again.
//
// Every storage access is wrapped: storage can throw (private mode, blocked site data), and
// a failure must degrade to "no preference recorded", never break the page.

const AUTH_HINT_KEY = "zenfresh-auth-hint";
const ENTRY_KEY = "zenfresh-entry";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Ignored on purpose: see the note at the top of this file.
  }
}

/**
 * A cheap "this browser has signed in before" marker. It lets the app skip loading Firebase
 * Auth (a large chunk) for visitors who have never logged in; it is a hint only and grants
 * nothing — the real session lives in Firebase Auth.
 */
export const hasAuthHint = () => read(AUTH_HINT_KEY) === "1";
export const setAuthHint = () => write(AUTH_HINT_KEY, "1");
export const clearAuthHint = () => write(AUTH_HINT_KEY, null);

// Also remembered in memory: if storage is blocked the choice still holds for this session.
let guestChosenThisSession = false;
export const hasChosenGuest = () => guestChosenThisSession || read(ENTRY_KEY) === "guest";
export const chooseGuest = () => {
  guestChosenThisSession = true;
  write(ENTRY_KEY, "guest");
};

/**
 * Crawlers, Lighthouse and browser automation have no stored choice, so gating them would
 * make the home page unindexable (and Lighthouse CI would audit the login page instead of
 * the home page). They are never redirected.
 */
export function isAutomatedClient(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.webdriver || /bot|crawl|spider|lighthouse|headless|prerender/i.test(navigator.userAgent);
}

/** True only for a real, first-time visitor who has not yet made a choice. */
export function shouldShowWelcome(): boolean {
  return !hasAuthHint() && !hasChosenGuest() && !isAutomatedClient();
}
